// feePaymentLedger.js — pure aggregation helpers over "Fee Payments" ledger
// rows (see FEE_PAYMENT_COLUMNS in backend/sheetColumns.js). Shared by the
// admin portal (admin/registration/paymentCalculations.js, which owns the
// owed-side math) and the teacher portal (Dancers.js's payment breakdown),
// which are otherwise kept isolated from each other's code — this is the one
// piece both legitimately need, so it lives here instead of being duplicated.
//
// entryTitle/participantId are blank on a plain whole-school lump payment
// (today's original behavior) — every helper here treats "blank" as "not
// attributable to this entry/participant," never as an accidental match.

export function paidForEntry(entryTitle, payments) {
  if (!entryTitle) return 0;
  return (payments || [])
    .filter(p => p.category === 'entry' && p.entryTitle === entryTitle)
    .reduce((sum, p) => sum + p.amount, 0);
}

export function paidForEntryAndParticipant(entryTitle, participantId, payments) {
  if (!entryTitle || !participantId) return 0;
  return (payments || [])
    .filter(p => p.category === 'entry' && p.entryTitle === entryTitle && p.participantId === participantId)
    .reduce((sum, p) => sum + p.amount, 0);
}

// A participant's total Entry Fee paid, across every entry they're in.
export function paidForParticipant(participantId, payments) {
  if (!participantId) return 0;
  return (payments || [])
    .filter(p => p.category === 'entry' && p.participantId === participantId)
    .reduce((sum, p) => sum + p.amount, 0);
}

// Old-style whole-school lump Entry Fee payments — not attributable to any
// one entry, so left out of every per-entry/per-participant breakdown above.
export function unattributedEntryPayments(payments) {
  return (payments || [])
    .filter(p => p.category === 'entry' && !p.entryTitle)
    .reduce((sum, p) => sum + p.amount, 0);
}

// A dancer's tracked credit — money from a by-participant Entry Fee payment
// that had nowhere left to go (their entries and Participation Fee were both
// already fully paid). See handleApplyEntryFeeByParticipant.
export function creditFor(participantId, payments) {
  if (!participantId) return 0;
  return (payments || [])
    .filter(p => p.category === 'credit' && p.participantId === participantId)
    .reduce((sum, p) => sum + p.amount, 0);
}

// The school's fungible credit balance — money owed back to them from a
// deleted paid participant/entry (or an entry left behind during a "replace
// dancer"), not yet applied to anything. Unlike creditFor above, this is
// school-wide, not scoped to one participant, and a grant/application pair
// is just a positive/negative row in the same category rather than two
// different ones — see appendFeePaymentRow / POST /api/teacher/dancers/:id/replace.
export function schoolCreditBalance(payments) {
  return (payments || [])
    .filter(p => p.category === 'school-credit')
    .reduce((sum, p) => sum + p.amount, 0);
}
